import React from "react";
import { Button, Row, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import "react-image-crop/dist/ReactCrop.css";

import defaultAvatar from "assets/img/default-avatar.png";
import { notificationMessageActions } from "_actions";
import CircularImageCropper from "./CircularImageCropper";

// Minimum natural image size required before cropping
const MIN_DIMENSION = 150; // px
const MAX_IMAGE_BYTES = 7_500_000; // 7.5 MB

class ProfileImageUpload extends React.Component {
  fileInputRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      // Source & preview
      src: null, // selected image (dataURL) for cropper
      imagePreviewUrl: props.avatar
        ? defaultAvatar
        : props.imageUrl
          ? props.imageUrl
          : defaultAvatar,

      // Cropper output
      croppedImageUrl: null, // dataURL returned by cropper
      blob: null,            // Blob returned by cropper

      // UI
      modalNotice: false,
      error: "",
      index: props.index !== undefined ? props.index : null,
    };
  }

  // ---------- File selection ----------
  onSelectFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      this.props.dispatch(
        notificationMessageActions.addNotificationToQueue(
          "Please select a JPG or PNG image.",
          2
        )
      );
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      this.props.dispatch(
        notificationMessageActions.addNotificationToQueue(
          "Your profile picture must be less than 7.5 MB.",
          2
        )
      );
      return;
    }

    try {
      const dataUrl = await this.readFileAsDataURL(file);
      // Probe natural dimensions
      await this.ensureMinDimensions(dataUrl, MIN_DIMENSION, MIN_DIMENSION);
      this.setState({
        src: dataUrl,
        modalNotice: true,
        error: "",
        // reset any previous crop result
        croppedImageUrl: null,
        blob: null,
      });
    } catch (err) {
      const msg = err?.message || "Invalid image selected.";
      this.setState({ error: msg, src: null, modalNotice: false });
      this.props.dispatch(
        notificationMessageActions.addNotificationToQueue(msg, 2)
      );
    }
  };

  readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = () => resolve(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  }

  ensureMinDimensions(dataUrl, minW, minH) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth >= minW && img.naturalHeight >= minH) resolve();
        else reject(new Error(`Image must be at least ${minW} x ${minH} pixels.`));
      };
      img.onerror = () => reject(new Error("Unable to load image"));
      img.src = dataUrl;
    });
  }

  // ---------- Modal controls ----------
  toggleModalNotice = () =>
    this.setState((s) => ({ modalNotice: !s.modalNotice, error: "" }));

  closeModalNotice = () => {
    this.setState({
      modalNotice: false,
      src: null,
      error: "",
      // keep cropped result so user can Save after closing, if you want
    });
  };

  handleClick = () => {
    this.fileInputRef.current?.click();
  };

  // Called by the cropper when user finishes cropping
  updateAvatar = (dataUrl, blob) => {
    this.setState({ croppedImageUrl: dataUrl, blob });
  };

  // Persist the cropped result into preview + notify parent
  handleImageChange = (e) => {
    e?.preventDefault?.();
    const { blob, croppedImageUrl } = this.state;
    if (!blob || !croppedImageUrl) return;

    this.setState(
      {
        imagePreviewUrl: croppedImageUrl,
        modalNotice: false,
        src: null,
        error: "",
      },
      () => {
        // Notify parent with new blob / preview
        this.props.callback?.({ state: this.state, newImage: true });
      }
    );
  };

  render() {
    const {
      imagePreviewUrl,
      modalNotice,
      src,
      croppedImageUrl,
      blob,
      error,
    } = this.state;
    const prefersDark = localStorage.getItem("darkMode") === "true";

    return (
      <div className="fileinput text-center">
        {/* Hidden file input */}
        <input
          type="file"
          accept=".jpg,.jpeg,.png"
          onChange={this.onSelectFile}
          ref={this.fileInputRef}
          style={{ display: "none" }}
        />

        {/* Trigger */}
        <div>
          <img
            className="rounded-circle cursor-pointer avatar-profile"
            onClick={this.handleClick}
            src={imagePreviewUrl}
            alt="Profile"
          />
        </div>

        {/* Optional inline error */}
        {error ? (
          <div className="text-danger small mt-2">{error}</div>
        ) : null}

        {/* Crop modal */}
        <Row>
          <Modal
            isOpen={modalNotice}
            toggle={this.toggleModalNotice}
            className="modal-notice"
            size="lg"
            fade={false}
            onClosed={this.closeModalNotice}
          >
            <ModalHeader toggle={this.toggleModalNotice}>
              Update Profile Picture
            </ModalHeader>

            <ModalBody>
              {/* Pass the selected image to the cropper */}
              <CircularImageCropper
                src={src}                 // <— make sure the cropper accepts this prop
                minDimension={MIN_DIMENSION}
                aspect={1}
                updateAvatar={this.updateAvatar} // cropper calls this with (dataUrl, blob)
              />
            </ModalBody>

            <ModalFooter className="text-center">
              <Button
                size="sm"
                color={prefersDark ? "spc-white" : "default"}
                onClick={this.toggleModalNotice}
                outline
              >
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary"
                onClick={this.handleImageChange}
                disabled={!croppedImageUrl || !blob}
              >
                Save
              </Button>
            </ModalFooter>
          </Modal>
        </Row>
      </div>
    );
  }
}

export default ProfileImageUpload;